import { useCallback, useRef, useEffect } from 'react';

export interface UseDragSelectOptions {
  selectedRows: Set<string>;
  onSelectionChange?: (selectedIds: string[]) => void;
}

export interface UseDragSelectReturn {
  isDraggingRef: React.MutableRefObject<boolean>;
  dragStartIndexRef: React.MutableRefObject<number | null>;
  initialSelectionRef: React.MutableRefObject<Set<string>>;
  mouseDownRowRef: React.MutableRefObject<string | null>;
  hasMovedRef: React.MutableRefObject<boolean>;
  handleCheckboxMouseDown: (e: React.MouseEvent, rowId: string, index: number, allRowIds: string[]) => void;
  handleRowMouseEnter: (rowId: string, index: number, allRowIds: string[]) => void;
  handleRowMouseMove: (rowId: string, index: number, allRowIds: string[]) => void;
}

/**
 * Custom hook for managing drag-to-select functionality in tables
 * Allows users to click and drag across checkboxes to select/deselect ranges of rows
 * 
 * @example
 * ```tsx
 * const {
 *   handleCheckboxMouseDown,
 *   handleRowMouseEnter,
 *   handleRowMouseMove,
 *   isDraggingRef,
 *   hasMovedRef
 * } = useDragSelect({
 *   selectedRows,
 *   onSelectionChange
 * });
 * ```
 */
export function useDragSelect({
  selectedRows,
  onSelectionChange
}: UseDragSelectOptions): UseDragSelectReturn {
  // Range-select state
  const isDraggingRef = useRef<boolean>(false);
  const dragStartIndexRef = useRef<number | null>(null);
  const initialSelectionRef = useRef<Set<string>>(new Set());
  const mouseDownRowRef = useRef<string | null>(null);
  const hasMovedRef = useRef<boolean>(false);

  // Range-select handlers
  const handleCheckboxMouseDown = useCallback((e: React.MouseEvent, rowId: string, index: number, allRowIds: string[]) => {
    // Save initial selection state
    initialSelectionRef.current = new Set(selectedRows);
    mouseDownRowRef.current = rowId;
    dragStartIndexRef.current = index;
    hasMovedRef.current = false;
    isDraggingRef.current = false;
    
    // Don't prevent default or apply selection here - let normal click work
    // Selection will be applied when drag is detected (mouseenter on different row)
  }, [selectedRows]);

  const handleRowMouseEnter = useCallback((rowId: string, index: number, allRowIds: string[]) => {
    // If mouse is down and we've moved to a different row, activate drag mode
    if (mouseDownRowRef.current !== null && dragStartIndexRef.current !== null) {
      // Check if we're on a different row (drag detected)
      if (dragStartIndexRef.current !== index || hasMovedRef.current) {
        hasMovedRef.current = true;
        isDraggingRef.current = true;
        
        // Calculate range from start to current
        const startIndex = dragStartIndexRef.current;
        const endIndex = index;
        const minIndex = Math.min(startIndex, endIndex);
        const maxIndex = Math.max(startIndex, endIndex);
        
        // Determine target state based on initial click state
        const startRowId = allRowIds[startIndex];
        const wasStartSelected = initialSelectionRef.current.has(startRowId);
        const targetState = !wasStartSelected; // If start was unselected, select range; if selected, deselect range
        
        // Create new selection based on initial snapshot
        const newSelection = new Set(initialSelectionRef.current);
        
        // Apply selection to the range
        for (let i = minIndex; i <= maxIndex; i++) {
          const currentRowId = allRowIds[i];
          if (targetState) {
            newSelection.add(currentRowId);
          } else {
            newSelection.delete(currentRowId);
          }
        }
        
        // Update selection
        onSelectionChange?.(Array.from(newSelection));
      }
    }
  }, [onSelectionChange]);

  const handleRowMouseMove = useCallback((rowId: string, index: number, allRowIds: string[]) => {
    // If mouse is down, mark as moved
    if (mouseDownRowRef.current !== null) {
      hasMovedRef.current = true;
    }
    
    // Apply same range logic on mousemove (for cases where mouseenter doesn't fire)
    if (isDraggingRef.current && dragStartIndexRef.current !== null) {
      const startIndex = dragStartIndexRef.current;
      const endIndex = index;
      const minIndex = Math.min(startIndex, endIndex);
      const maxIndex = Math.max(startIndex, endIndex);
      
      // Determine target state based on initial click state
      const startRowId = allRowIds[startIndex];
      const wasStartSelected = initialSelectionRef.current.has(startRowId);
      const targetState = !wasStartSelected;
      
      // Create new selection based on initial snapshot
      const newSelection = new Set(initialSelectionRef.current);
      
      // Apply selection to the range
      for (let i = minIndex; i <= maxIndex; i++) {
        const currentRowId = allRowIds[i];
        if (targetState) {
          newSelection.add(currentRowId);
        } else {
          newSelection.delete(currentRowId);
        }
      }
      
      // Update selection
      onSelectionChange?.(Array.from(newSelection));
    }
  }, [onSelectionChange]);

  // Cleanup drag mode on mouseup
  useEffect(() => {
    const handleMouseUp = () => {
      // If we never moved, it was just a click - already handled in mousedown
      isDraggingRef.current = false;
      dragStartIndexRef.current = null;
      mouseDownRowRef.current = null;
      hasMovedRef.current = false;
      initialSelectionRef.current = new Set();
    };

    window.addEventListener('mouseup', handleMouseUp);
    return () => {
      window.removeEventListener('mouseup', handleMouseUp);
    };
  }, []);

  return {
    isDraggingRef,
    dragStartIndexRef,
    initialSelectionRef,
    mouseDownRowRef,
    hasMovedRef,
    handleCheckboxMouseDown,
    handleRowMouseEnter,
    handleRowMouseMove
  };
}

